import { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import EmptyData from "./EmptyData";

function CustomWordCloud({ dataSelector, colorPalette, emptyDataText }) {
	const data = useSelector(dataSelector);
	const canvasRef = useRef(null);
	const containerRef = useRef(null);
	const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0 });

	useLayoutEffect(() => {
		if (!canvasRef.current || data?.length === 0) return;

		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const width = containerRef.current?.offsetWidth || 0;
		const height = containerRef.current?.offsetHeight || 0;

		// Set canvas resolution for crisp rendering
		const dpr = window.devicePixelRatio || 1;
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;
		ctx.scale(dpr, dpr);

		// Clear the canvas
		ctx.clearRect(0, 0, width, height);

		// Max and min font sizes with padding
		const maxFontSize = 60;
		const minFontSize = 18;
		const padding = 10; // Add padding from edges
		const maxFreq = Math.max(...data.map((w) => w.value));
		const minFreq = Math.min(...data.map((w) => w.value));

		// Spiral parameters
		const centerX = width / 2;
		const centerY = height / 2;
		const spiralGap = 5;
		let angle = 0;
		let radius = 0;

		// Color assignments
		const colorSteps = colorPalette.length;
		const stepSize = maxFreq > minFreq ? (maxFreq - minFreq) / colorSteps : 1;

		const getColorForWord = (wordValue) => {
			const groupIndex = Math.min(colorSteps - 1, Math.floor((maxFreq - wordValue) / stepSize));
			return colorPalette[groupIndex];
		};

		const wordRects = [];

		const placeWord = (word) => {
			const fontSize = maxFreq === minFreq ? maxFontSize : ((word.value - minFreq) / (maxFreq - minFreq)) * (maxFontSize - minFontSize) + minFontSize;

			ctx.font = `${fontSize}px Arial`;
			ctx.fillStyle = getColorForWord(word.value);
			const wordWidth = ctx.measureText(word.text).width;

			let x, y, isOverlapping, isOutOfBounds;
			let attempts = 0;
			const maxAttempts = 2000; // Increased attempts

			do {
				angle += 0.1;
				radius = spiralGap * Math.sqrt(angle); // Slower spiral growth

				x = centerX + radius * Math.cos(angle) - wordWidth / 2;
				y = centerY + radius * Math.sin(angle) + fontSize / 2;

				// Stricter boundary checking with padding
				isOutOfBounds = x < padding || x + wordWidth > width - padding || y - fontSize < padding || y > height - padding;

				isOverlapping = wordRects.some((rect) => checkOverlap(x, y - fontSize, wordWidth, fontSize, rect));

				attempts++;
			} while ((isOverlapping || isOutOfBounds) && attempts < maxAttempts);

			if (!isOverlapping && !isOutOfBounds) {
				ctx.fillText(word.text, x, y);
				wordRects.push({
					x,
					y: y - fontSize,
					width: wordWidth,
					height: fontSize,
					text: word.text,
					value: word.value,
				});
			}
		};

		const checkOverlap = (x, y, width, height, rect) => {
			const buffer = 2; // Small buffer between words
			return x < rect.x + rect.width + buffer && x + width + buffer > rect.x && y < rect.y + rect.height + buffer && y + height + buffer > rect.y;
		};

		data.forEach((word) => placeWord(word));

		const handleMouseMove = (e) => {
			const rect = canvas.getBoundingClientRect();
			const mouseX = e.clientX - rect.left;
			const mouseY = e.clientY - rect.top;

			const hoveredWord = wordRects.find((word) => mouseX >= word.x && mouseX <= word.x + word.width && mouseY >= word.y && mouseY <= word.y + word.height);

			if (hoveredWord) {
				setTooltip({
					visible: true,
					text: `${hoveredWord.text}: ${hoveredWord.value}`,
					x: Math.min(mouseX + 10, width - 100), // Keep tooltip in bounds
					y: Math.min(mouseY + 10, height - 20),
				});
			} else {
				setTooltip({ visible: false, text: "", x: 0, y: 0 });
			}
		};

		canvas.addEventListener("mousemove", handleMouseMove);
		return () => canvas.removeEventListener("mousemove", handleMouseMove);
	}, [data, colorPalette]);

	return (
		<>
			{data?.length > 0 ? (
				<div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
					<canvas ref={canvasRef} />
					{tooltip.visible && (
						<div
							style={{
								position: "absolute",
								left: tooltip.x,
								top: tooltip.y,
								background: "rgba(0, 0, 0, 0.7)",
								color: "#fff",
								padding: "5px",
								borderRadius: "3px",
								pointerEvents: "none",
								fontSize: "12px",
								fontWeight: "900",
							}}>
							{tooltip.text}
						</div>
					)}
				</div>
			) : (
				<EmptyDataContainer>
					<EmptyData text={emptyDataText} icon="CLOUD-WORD" />
				</EmptyDataContainer>
			)}
		</>
	);
}

CustomWordCloud.displayName = "CustomWordCloud";
export default CustomWordCloud;

const EmptyDataContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 400px;
	width: 100%;
`;
